<template>
    <div class="scorecard-template" :class="[backgroundColorClass]">
        <div v-if="chart_selected.data" class="mt-1 mx-2">
            <h1 
                class="scorecard-value" 
                :data-bs-toggle="shouldShowTooltip ? 'tooltip' : null"
                :data-bs-placement="'top'"
                :data-bs-title="shouldShowTooltip ? current_value : null"
            >
                <span class="scorecard-value-text">{{current_value}}</span>
                <span class="badge ms-2" v-if="variance != null"
                    :class="{ 'bg-green bg-icon-increase': (variance >= 0), 'bg-red bg-icon-decrease': (variance < 0)}"
                >{{percentage_na(variance)}}</span>
            </h1>       
            <p class="text-muted my-2">
                <img v-if="url" :src="url" :class="['metric-icon', 'me-1', iconClass]">     
                <img v-else-if="chart_selected.icon" :src="chart_selected.icon" :class="['metric-icon', 'me-1', iconClass]">
                <img v-else :src="getMetricIcon()" :class="['metric-icon', 'me-1', getIconColorClass()]">
                {{ chart_selected.title && chart_selected.title.trim() != '' ? chart_selected.title : metric_name}}
            </p>
            <p>{{chart_selected.description}}</p>
        </div>
        <div v-else-if="!if_refreshing" class='d-inline-block text-center'>
            <h1 class="text-secondary mb-1"><i class="fas fa-chart-simple"></i></h1>
            <h4 class="text-secondary mb-2"><em>Press the <strong>Preview</strong> button to render the chart</em></h4>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import filtersMixin from '../../../../mixins/filters';

export default defineComponent({
    name: 'ScorecardTemplate',
    mixins: [filtersMixin],
    props: {
        chart_selected: {
            type: Object,
            required: true
        },
        if_refreshing: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            current_value: null,
            metric_name: '',
            variance: null,
            local_chart_selected: null,
            shouldShowTooltip: false
        };
    },
    watch: {
        chart_selected: {
            handler(newVal) {
                if (newVal?.data) {
                    this.destroyTooltips();
                    this.$nextTick(() => {
                        this.renderChart();
                    });
                }
            },
            deep: true
        }
    },
    computed: {
        url() {
            if (!this.chart_selected?.subtitle || !this.chart_selected.subtitle.match(/\b\S+/gi)) {
                return null;
            }
            return this.chart_selected.subtitle;
        },
        backgroundColorClass() {
            const backgroundColor = this.chart_selected?.class_name;
            const metric = this.chart_selected?.metrics?.[0];
            
            // If icon_background_color is white or not set, don't add any class
            if (!backgroundColor || backgroundColor === 'white') {
                return '';
            }
            
            // If icon_background_color is passive_color, add class based on metric color
            if (backgroundColor === 'passive_color' && metric?.color) {
                const color = metric.color.toLowerCase();
                // Map color values to class names
                const colorMap = {
                    'yellow': 'passive-yellow',
                    'green': 'passive-green',
                    'blue': 'passive-blue',
                    'purple': 'passive-purple'
                };
                return colorMap[color] || '';
            }
            
            return '';
        },
        iconClass() {
            const backgroundColor = this.chart_selected?.class_name;
            const metric = this.chart_selected?.metrics?.[0];
            
            // If icon_background_color is white or not set, don't add any class
            if (!backgroundColor || backgroundColor === 'white') {
                return '';
            }
            
            // If icon_background_color is passive_color, add icon class based on metric color
            if (backgroundColor === 'passive_color' && metric?.color) {
                const color = metric.color.toLowerCase();
                // Map color values to icon class names
                const colorMap = {
                    'yellow': 'icon-yellow',
                    'green': 'icon-green',
                    'blue': 'icon-blue',
                    'purple': 'icon-purple'
                };
                return colorMap[color] || '';
            }
            
            return '';
        }
    },
    mounted() {
        if (this.chart_selected?.data) {
            this.$nextTick(() => {
                this.renderChart();
                this.initializeTooltips();
            });
        }
    },
    beforeUnmount() {
        this.destroyTooltips();
    },
    methods: {
        renderChart() {
            if (!this.chart_selected?.data) return;

            let newCurrentValue = null;
            let newVariance = null;

            // TODO: scorecard doesn't handle order_by right now, I am reversing the data array manually to get the latest value 
            Object.keys(this.chart_selected.data).forEach(key => {
                const reversedData = [...this.chart_selected.data[key]].reverse();
                newCurrentValue = reversedData[0];
                if (reversedData.length > 1) {
                    newVariance = 0;
                    if (reversedData[1] != null && reversedData[1] != 0) {
                        newVariance = (newCurrentValue - reversedData[1]) / reversedData[1];
                    }                        
                }
            });

            // Update values in a single pass
            this.current_value = this.formatValue(newCurrentValue);
            this.metric_name = this.chart_selected?.metrics?.[0]?.text || '';
            this.variance = newVariance;

            // Check if text is truncated after next DOM update
            this.$nextTick(() => {
                this.checkTextTruncation();
            });
        },
        checkTextTruncation() {
            const valueElement = this.$el.querySelector('.scorecard-value-text');
            if (valueElement) {
                this.shouldShowTooltip = valueElement.scrollWidth > valueElement.clientWidth;
                this.destroyTooltips();
                if (this.shouldShowTooltip) {
                    this.$nextTick(() => {
                        this.initializeTooltips();
                    });
                }
            }
        },
        initializeTooltips() {
            if (window.bootstrap && window.bootstrap.Tooltip) {
                this.$nextTick(() => {
                    const tooltipElements = this.$el.querySelectorAll('[data-bs-toggle="tooltip"]');
                    tooltipElements.forEach(element => {
                        const existingTooltip = window.bootstrap.Tooltip.getInstance(element);
                        if (existingTooltip) {
                            existingTooltip.dispose();
                        }
                        const title = element.getAttribute('data-bs-title') || element.getAttribute('title');
                        if (title) {
                            new window.bootstrap.Tooltip(element, {
                                title: title
                            });
                        }
                    });
                });
            }
        },
        destroyTooltips() {
            if (window.bootstrap && window.bootstrap.Tooltip) {
                const tooltipElements = this.$el.querySelectorAll('[data-bs-toggle="tooltip"]');
                tooltipElements.forEach(element => {
                    const tooltip = window.bootstrap.Tooltip.getInstance(element);
                    if (tooltip) {
                        tooltip.dispose();
                    }
                });
                
                document.querySelectorAll('.tooltip').forEach(el => {
                    if (el && el.parentNode) {
                        el.parentNode.removeChild(el);
                    }
                });
            }
        },
        formatDateValueForDisplay(value) {
            if (value == null || value === '') return null;
            let dateInput = value;
            const str = String(value).replace(/\D/g, '');
            if (str.length === 8) {
                dateInput = `${str.slice(0, 4)}-${str.slice(4, 6)}-${str.slice(6, 8)}`;
            }
            return this.date_long_abbrev(dateInput) || null;
        },
        formatValue(value) {
            if (value == null) return 'N/A';

            const format_type = this.chart_selected?.metric_formats?.[0] || 'number';

            switch (format_type) {
                case 'money':
                    return this.currency_na(value);
                case 'percent':
                    return this.percentage_na(value);
                case 'date':
                    return this.formatDateValueForDisplay(value) ?? this.number_na(value);
                default:
                    return this.number_na(value);
            }
        },
        getMetricIcon() {
            const metric = this.chart_selected?.metrics[0];
            const color = metric?.color?.toLowerCase() || '';
            const iconMap = {
                'blue': '/img/icons/avid-icons/metric-icon-donor.svg',
                'yellow': '/img/icons/avid-icons/metric-icon-gifts.svg',
                'green': '/img/icons/avid-icons/metric-icon-revenue.svg',
                'purple': '/img/icons/avid-icons/metric-icon-other.svg'
            };
            return iconMap[color] || '/img/icons/avid-icons/metric-icon-other.svg';
        },
        getIconColorClass() {
            const metric = this.chart_selected?.metrics[0];
            return metric?.color ? `icon-${metric.color.toLowerCase()}` : '';
        },
    }
});
</script>